/*
 * GNB 마크업의 언어별 선택.
 *
 * 메뉴는 SFC 가 아니라 raw HTML 을 v-html 로 넣는 구조라, content 처럼 $rt 바인딩을
 * 심을 수 없다. 대신 scripts/apply-i18n-pages.mjs 가 언어별로 구워 둔 HTML 을 고른다.
 * SiteNav(메뉴만)와 SiteHeader(껍데기+메뉴) 둘 다 여기를 통해 같은 HTML 을 쓴다.
 */
import ko from '../data/nav-dist/ko.html?raw'
import en from '../data/nav-dist/en.html?raw'
import ja from '../data/nav-dist/ja.html?raw'
import vi from '../data/nav-dist/vi.html?raw'
import es from '../data/nav-dist/es.html?raw'
import { RENEWAL_FALLBACK_LOCALE, type RenewalLocale } from './useRenewalText'
import type { RenewalInsight } from './useRenewalInsights'

const NAV_HTML: Record<RenewalLocale, string> = { ko, en, ja, vi, es }

export function renewalNavHtml(locale: RenewalLocale): string {
  return NAV_HTML[locale] ?? NAV_HTML[RENEWAL_FALLBACK_LOCALE]
}

// ─── 최근 소식 ──────────────────────────────────────────────────────────────
// 마크업에 박아 둔 기본 항목은 금방 낡는다. 데이터는 호스트가 주고(단독이면 자체
// /api/insights) 여기서 같은 자리에 끼워 넣는다. SiteNav 와 SiteHeader 가 같은 함수를
// 쓰므로 단독과 얹힌 상태의 결과가 갈라지지 않는다.

const NAV_NEWS_LIST = /(<ul class="nav-news-list">)[\s\S]*?(<\/ul>)/
// main 이 붙여 둔 고정 훅. src 값만 갈아끼운다.
const NAV_NEWS_THUMB = /(<img class="nav-news-thumb" src=")[^"]*(")/

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// 메뉴의 소식은 늘 올해 최신 글이라 연도를 뺀다. ko·ja 10/29, vi 29/10, en Oct 29, es 29 oct
const navNewsDate = (date: string, locale: RenewalLocale) => {
  const [, mm, dd] = date.split('-')
  if (!dd) return date
  if (locale === 'ko' || locale === 'ja') return `${mm}/${dd}`
  if (locale === 'vi') return `${dd}/${mm}`
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

// 제목을 2줄 가득에서 날짜 자리만큼만 남기고 자른다. 날짜는 CSS float 로 둘째 줄 오른쪽 끝에 붙는다.
// 폭 단위는 한글·한자·가나 2, 그 외 1 (14px 기준 1단위 ≈ 6.5px).
// 2줄 × 292px(316px 칸 - 좌우 여백) - 날짜 자리 40px(날짜 약 32px + 간격 8px) ≈ 83단위.
// ponytail: 글꼴 폭을 재지 않는 근사치. 넘치면 3번째 줄은 칸 높이에서 잘리니 값을 낮춘다.
const NEWS_TITLE_BUDGET = 83
const WIDE_CHAR = /[ᄀ-ᇿ⺀-鿿가-힯＀-￯]/

const clipNewsTitle = (title: string) => {
  let width = 0
  const chars = Array.from(title)
  for (let i = 0; i < chars.length; i++) {
    width += WIDE_CHAR.test(chars[i]) ? 2 : 1
    if (width > NEWS_TITLE_BUDGET) return chars.slice(0, i).join('').trimEnd() + '…'
  }
  return title
}

const newsItemHtml = (insight: RenewalInsight, locale: RenewalLocale) =>
  `<li><a class="nav-news-item" href="${escapeHtml(insight.url)}" title="${escapeHtml(insight.title)}" target="_blank" rel="noopener">`
    + `<span class="nav-news-title">${escapeHtml(clipNewsTitle(insight.title))}</span>`
    + `<span class="nav-news-meta">${escapeHtml(navNewsDate(insight.date, locale))}</span></a></li>`

/** 받아 온 소식으로 "최근 소식" 목록과 배너 썸네일을 채운다.
 *  못 받았으면 마크업의 기본 항목을 그대로 둔다 — 메뉴가 비는 것보다 낫다. */
export function applyNavInsights(html: string, insights: readonly RenewalInsight[], locale: RenewalLocale): string {
  if (!insights.length) return html
  return html
    .replace(NAV_NEWS_LIST, (_, open: string, close: string) => open + insights.map(insight => newsItemHtml(insight, locale)).join('') + close)
    .replace(NAV_NEWS_THUMB, (_, open: string, close: string) => open + escapeHtml(insights[0].image ?? '') + close)
}
